"use client";

import { useMemo } from "react";
import { useProduct } from "@/contexts/ProductContext";
import {
  BIRCH_GALLERY_OPTION_NAMES,
  getFirstShopifyOptionByName,
} from "@/components/blocks/variantOptions/shopifyOptionConventions";
import { getSelectedOptionValue } from "@/utils/product/product-context-helpers";

/**
 * Normalise a Contentstack media item.
 * Supports: { image: { url, title, description } } and flat { url, title, description }
 */
const normalise = (item) => {
  const img = item?.image ?? item;
  return {
    uid: img?.uid || item?.uid,
    url: img?.url || item?.url || "",
    alt: img?.description || item?.description || img?.title || item?.title || "",
    tags: img?.tags || item?.tags || [],
    width: img?.dimension?.width,
    height: img?.dimension?.height,
  };
};

// Filters out images tagged for other colors.
const filterImagesByColorTag = (images, selectedColor) => {
  if (!selectedColor) return images;

  return images.filter((img) => {
    const tags = img.tags.map((t) => t.toLowerCase());
    return (
      !tags.some((t) => t.startsWith("gallery-")) ||
      tags.includes("gallery-all") ||
      tags.includes(`gallery-${selectedColor}`)
    );
  });
};

// Shared by both PDP galleries. galleryKey changes when the colour filter
// changes the image set, so a stateful gallery can remount and reset its
// active slide instead of pointing past the end of the new list.
export const useGalleryImages = (imagesProp) => {
  const { selectedVariant, productData } = useProduct();

  const selectedColor = useMemo(() => {
    const galleryOption = getFirstShopifyOptionByName(
      productData?.shopifyProduct,
      BIRCH_GALLERY_OPTION_NAMES,
    );
    if (!galleryOption) return null;

    const currentValue = getSelectedOptionValue(selectedVariant, galleryOption.name);
    return currentValue ? currentValue.toLowerCase() : null;
  }, [productData?.shopifyProduct, selectedVariant]);

  const images = useMemo(() => {
    const list = Array.isArray(imagesProp) ? imagesProp : [];
    const base = list.map(normalise).filter((img) => img.url);
    return filterImagesByColorTag(base, selectedColor);
  }, [imagesProp, selectedColor]);

  // Literal move: keep `i.uid`, same as the original. Not `i.uid || i.url`.
  return { images, galleryKey: images.map((i) => i.uid).join(",") };
};
